import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const IMAGES = [
  { id: 1, src: '/bedroom.webp', alt: 'Lodge interior' },
  { id: 2, src: '/terrace.webp', alt: 'Villa exterior' },
  { id: 3, src: '/coast.webp', alt: 'Ocean view' },
  { id: 4, src: '/dining.webp', alt: 'Restaurant' },
  { id: 5, src: '/barefoot1.webp', alt: 'Beach bar' },
  { id: 6, src: '/mountain.webp', alt: 'Mountain hiking' },
  { id: 7, src: '/village.webp', alt: 'Local village' },
  { id: 8, src: '/villa.webp', alt: 'Villa pool' },
];

const ScrollArrow = ({ direction, onClick }) => {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  const position = direction === 'left' ? 'left-2 md:left-4' : 'right-2 md:right-4';

  return (
    <button
      onClick={onClick}
      className={`absolute ${position} top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white flex items-center justify-center shadow-lg md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300`}
      data-testid={`gallery-scroll-${direction}`}
    >
      <Icon size={20} className="text-black md:hidden" />
      <Icon size={24} className="text-black hidden md:block" />
    </button>
  );
};

const GalleryLightbox = ({ image, onClose }) => (
  <AnimatePresence>
    {image && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
        onClick={onClose}
        data-testid="gallery-lightbox"
      >
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors duration-300"
          data-testid="gallery-lightbox-close"
          aria-label="Close"
        >
          <X size={32} />
        </button>
        <motion.img
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.3 }}
          src={image.src}
          alt={image.alt}
          className="max-w-full max-h-[85vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    )}
  </AnimatePresence>
);

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section 
      id="gallery" 
      className="py-4 md:py-6 bg-white" 
      data-testid="gallery-section"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Horizontal Scroll Gallery */}
        <div className="relative group">
          <ScrollArrow direction="left" onClick={() => scroll('left')} />
          <ScrollArrow direction="right" onClick={() => scroll('right')} />

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide px-6 md:px-12 pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {IMAGES.map((image) => (
              <button
                key={image.id}
                onClick={() => setSelectedImage(image)}
                className="flex-shrink-0 relative overflow-hidden cursor-pointer group/item"
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  loading="lazy"
                  className="h-[300px] md:h-[400px] w-auto object-cover transition-transform duration-700 group-hover/item:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover/item:bg-black/20 transition-colors duration-300" />
              </button>
            ))}
          </div>
        </div>

        <GalleryLightbox image={selectedImage} onClose={() => setSelectedImage(null)} />
      </div>
    </section>
  );
};

export default Gallery;
